// src/components/cards/BookingDetailCard.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import {
  Event as DateIcon,
  CalendarMonth as LengthIcon,
  Description as NotesIcon,
  Info as StatusIcon,
  Pets as PetsIcon,
  Email as EmailIcon,
  Assignment as FormIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ResponsiveFormGrid } from '../forms/shared';
import {
  BeingDetailsField,
  MetadataField,
} from '../forms/shared/DisplayFieldVariants';
import {
  FieldLabelContainerMultiline,
  FieldLabel,
  FieldInputContainerMultiline,
  FormFieldRowMultiline,
  SectionHeader,
  SectionHeaderWrapper,
} from '../forms/shared';
import { FloatingAccessButton } from '../common/FloatingAccessButton';
import BookingCardPetsList from '../forms/shared/BookingCardPetsList';
import BookingCardKennelList from '../forms/shared/BookingCardKennelList';
import BookingCardSurchargeList from '../forms/shared/BookingCardSurchargeList';
import BookingCardTotals from '../forms/shared/BookingCardTotals';
import BookingCardInvoiceHistory from '../forms/shared/BookingCardInvoiceHistory';
import BottomMenu, { BottomMenuItem } from '../menus/BottomMenu';
import { useErrorHandler } from '../../errors';
import {
  bookingsAPI,
  BookingFormEmailLogEntry,
  invoiceAPI,
  InvoiceEmailLogEntry,
} from '../../services/api';
import { useGlobalToast } from '../../App';
import { useHeaderDrawer } from '../../contexts/HeaderDrawerContext';
import { useAnchorRect } from '../../hooks/useAnchorRect';
import EmailInvoiceDialog from './shared/dialogs/EmailInvoiceDialog';
import EmailBookingFormDialog from './shared/dialogs/EmailBookingFormDialog';
import { getCustomIcon } from '../../utils/customIcons';
import { useAuth } from '../../contexts/AuthContext';
import { Permissions } from '../../utils/permissions';

interface BookingPetResponse {
  id: number;
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
  petSize: string;
  isSketch?: boolean;
  useSharingRate: boolean;
  hasHeater: boolean;
  dailyRate: number;
  numberOfNights: number;
  accommodationTotal: number;
  groomingTotal: number;
  petTotal: number;
  lineItemDiscount?: number;
  lineItemDiscountPercentage?: number;
  rateType: string | null;
}

interface BookingKennelResponse {
  id: number;
  kennelNumber: number;
  kennelSide: string;
  kennelSideId: number;
  size?: string;
  heaterNights: number;
  heaterDailyRate: number;
}

interface BookingSurchargeResponse {
  id: number;
  reason: string;
  amount: number;
}

interface BookingDetailCardProps {
  booking: {
    id: number;
    clientId: number;
    clientName: string;
    clientEmail?: string;
    checkInDate: string;
    checkOutDate: string;
    numberOfNights: number;
    status: string;
    notes: string;
    subTotal: number;
    discountAmount: number;
    taxAmount: number;
    totalAmount: number;
    depositAmount?: number;
    createdAt: string;
    updatedAt?: string;
    createdFromQuoteId?: number;
    pets: BookingPetResponse[];
    kennels?: BookingKennelResponse[];
    surcharges?: BookingSurchargeResponse[];
  };
  clientEmail?: string;
  onEdit?: () => void;
  onRefresh?: () => void;
}

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (isoString: string): string => {
  return new Date(isoString).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getPetTypeSummary = (pets: BookingPetResponse[]): string => {
  const petTypeMap = new Map<string, number>();
  pets.forEach((pet) => {
    const count = petTypeMap.get(pet.petType) || 0;
    petTypeMap.set(pet.petType, count + 1);
  });
  return Array.from(petTypeMap.entries())
    .map(([type, count]) => `${count} ${type}${count !== 1 ? 's' : ''}`)
    .join(', ');
};

const getPetSummaryWithNames = (pets: BookingPetResponse[]): string => {
  return getPetTypeSummary(pets);
};

const BookingDetailCard: React.FC<BookingDetailCardProps> = ({
  booking,
  clientEmail,
  onEdit,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();
  const { isHeaderDrawerOpen } = useHeaderDrawer();
  const { user } = useAuth();
  // Staff is view-only: no invoice or booking-form data, no actions.
  const canManage = Permissions.canEditBookings(user?.role);

  // Invoice email state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [emailLog, setEmailLog] = useState<InvoiceEmailLogEntry[]>([]);

  // Booking form email state (kept for the history "send now" link)
  const [bookingFormDialogOpen, setBookingFormDialogOpen] = useState(false);
  const [bookingFormSending, setBookingFormSending] = useState(false);
  const [bookingFormEmailLog, setBookingFormEmailLog] = useState<
    BookingFormEmailLogEntry[]
  >([]);

  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const bottomMenuFabRef = useRef<HTMLDivElement>(null);
  const bottomMenuAnchor = useAnchorRect(bottomMenuFabRef, bottomMenuOpen);

  const resolvedClientEmail = booking.clientEmail || clientEmail;

  const loadEmailLog = useCallback(async (invId: number) => {
    try {
      const log = await invoiceAPI.getEmailLog(invId);
      setEmailLog(log);
    } catch {
      // non-critical
    }
  }, []);

  const loadBookingFormEmailLog = useCallback(async () => {
    try {
      const log = await bookingsAPI.getBookingFormLog(booking.id);
      setBookingFormEmailLog(log);
    } catch {
      // non-critical
    }
  }, [booking.id]);

  useEffect(() => {
    if (!canManage) return;
    loadBookingFormEmailLog();

    let cancelled = false;
    const fetchInvoice = async () => {
      try {
        const invoices = await invoiceAPI.getByBooking(booking.id);
        if (!cancelled && invoices.length) {
          setInvoiceId(invoices[0].id);
          loadEmailLog(invoices[0].id);
        }
      } catch {
        // non-critical
      }
    };
    fetchInvoice();
    return () => {
      cancelled = true;
    };
  }, [booking.id, canManage, loadEmailLog, loadBookingFormEmailLog]);

  const getBookingMetadataValue = (booking: any) => {
    const createdDate = booking.createdAt
      ? new Date(booking.createdAt).toLocaleString()
      : 'Unknown';

    const createdAtISO = booking.createdAt
      ? new Date(booking.createdAt).toISOString()
      : null;
    const updatedAtISO = booking.updatedAt
      ? new Date(booking.updatedAt).toISOString()
      : null;

    const hasUpdate =
      updatedAtISO && createdAtISO && updatedAtISO !== createdAtISO;

    return hasUpdate && booking.updatedAt ? (
      <>
        Created: {createdDate}
        <br />
        Updated: {new Date(booking.updatedAt).toLocaleString()}
      </>
    ) : (
      <Box component="span" sx={{ display: 'block', padding: '0.75em 0' }}>
        Created: {createdDate}
      </Box>
    );
  };

  const handleEditClick = () => {
    navigate(`/booking/${booking.id}/edit`);
  };

  const handleDownloadInvoice = async () => {
    try {
      const invoices = await invoiceAPI.getByBooking(booking.id);
      if (!invoices.length) {
        handleError(new Error('No invoice found for this booking'), {
          context: 'downloading invoice PDF',
          showToast: true,
        });
        return;
      }
      const pdfBlob = await invoiceAPI.downloadPdf(invoices[0].id);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      const sanitizedName = booking.clientName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      link.href = url;
      link.download = `Invoice_${invoices[0].invoiceNumber}_${sanitizedName}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, {
        context: 'downloading invoice PDF',
        showToast: true,
      });
    }
  };

  const handleViewAccount = () => {
    navigate(`/clients/${booking.clientId}/account`, {
      state: { accountTab: 0 },
    });
  };

  const handleEmailInvoice = async () => {
    setEmailSending(true);
    try {
      const invoices = await invoiceAPI.getByBooking(booking.id);
      if (!invoices.length) {
        handleError(new Error('No invoice found for this booking'), {
          context: 'emailing invoice',
          showToast: true,
        });
        return;
      }
      const invId = invoices[0].id;
      await invoiceAPI.emailInvoice(invId);
      showSuccess();
      setInvoiceId(invId);
      loadEmailLog(invId);
    } catch (error) {
      handleError(error, { context: 'emailing invoice', showToast: false });
      showError();
    } finally {
      setEmailSending(false);
      setEmailDialogOpen(false);
    }
  };

  const handleSendBookingForm = async () => {
    setBookingFormSending(true);
    try {
      await bookingsAPI.sendBookingForm(booking.id);
      showSuccess();
      loadBookingFormEmailLog();
    } catch (error) {
      handleError(error, { context: 'sending booking form', showToast: false });
      showError();
    } finally {
      setBookingFormSending(false);
      setBookingFormDialogOpen(false);
    }
  };

  const invoiceSummaryValue = (() => {
    const successCount = emailLog.filter((e) => e.success).length;
    if (!successCount) return 'Unsent';
    const last = emailLog.find((e) => e.success);
    if (!last) return 'Unsent';
    if (successCount === 1) return `Sent ${formatDateTime(last.sentAt)}`;
    return `Sent ${successCount} times - last ${formatDateTime(last.sentAt)}`;
  })();

  const bookingFormSummaryValue = (() => {
    const successCount = bookingFormEmailLog.filter((e) => e.success).length;
    if (!successCount) return 'Unsent';
    const last = bookingFormEmailLog.find((e) => e.success);
    if (!last) return 'Unsent';
    if (successCount === 1) return `Sent ${formatDateTime(last.sentAt)}`;
    return `Sent ${successCount} times - last ${formatDateTime(last.sentAt)}`;
  })();

  const bookingMenuItems: BottomMenuItem[] = [
    {
      key: 'download-invoice',
      label: 'Download Invoice',
      onClick: handleDownloadInvoice,
    },
    {
      key: 'email-invoice',
      label: 'Email Invoice',
      onClick: () => setEmailDialogOpen(true),
      disabled: !resolvedClientEmail,
    },
    {
      key: 'view-account',
      label: 'Account',
      onClick: handleViewAccount,
    },
    {
      key: 'edit-booking',
      label: 'Edit Booking',
      onClick: handleEditClick,
    },
  ];

  return (
    <StyledCard>
      <Box>
        <ResponsiveFormGrid
          alternateRowColors
          breakpointPadding={{ default: '0', medium: '0', large: '0' }}
          container
          spacing={0}
        >
          <BeingDetailsField
            label="Check-in"
            valuePadding="2.4px 0 2.4px 1rem"
            value={formatDate(booking.checkInDate)}
            startIcon={<DateIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Check-out"
            valuePadding="2.4px 0 2.4px 1rem"
            value={formatDate(booking.checkOutDate)}
            startIcon={<DateIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Length"
            valuePadding="2.4px 0 2.4px 1rem"
            value={`${booking.numberOfNights} night${booking.numberOfNights !== 1 ? 's' : ''}`}
            startIcon={<LengthIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Status"
            valuePadding="2.4px 0 2.4px 1rem"
            value={booking.status}
            startIcon={<StatusIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Pets"
            valuePadding="2.4px 0 2.4px 1rem"
            value={getPetSummaryWithNames(booking.pets)}
            startIcon={<PetsIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          {canManage && (
            <BeingDetailsField
              label="Form"
              valuePadding="2.4px 0 2.4px 1rem"
              value={bookingFormSummaryValue}
              startIcon={<FormIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {canManage && (
            <BeingDetailsField
              label="Invoice"
              valuePadding="2.4px 0 2.4px 1rem"
              value={invoiceSummaryValue}
              startIcon={<EmailIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {booking.notes && (
            <BeingDetailsField
              label="Notes"
              valuePadding="2.4px 0 2.4px 1rem"
              value={booking.notes}
              startIcon={<NotesIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {booking.createdFromQuoteId && (
            <BeingDetailsField
              label="Converted"
              valuePadding="2.4px 0 2.4px 1rem"
              value={
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/quote/${booking.createdFromQuoteId}`)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/quote/${booking.createdFromQuoteId}`);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                    whiteSpace: 'nowrap',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    display: 'block',
                    width: '98%',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {isMobile ? 'Converted ' : ''}
                  {`Quote #${booking.createdFromQuoteId}`}
                  {` on ${formatDate(booking.createdAt)}`}
                </Box>
              }
              startIcon={getCustomIcon('convert', '2.4rem')}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}
        </ResponsiveFormGrid>
      </Box>

      {/* Pets Section */}
      {booking.pets.length > 0 && (
        <>
          <SectionHeaderWrapper>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Pets</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{
                pt: 0,
                pr: 0,
                pb: 0,
                pl: 0,
                '@media (max-width: 600px)': { padding: 0 },
              }}
            >
              <BookingCardPetsList pets={booking.pets} />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Kennels Section */}
      {(booking.kennels?.length ?? 0) > 0 && (
        <>
          <SectionHeaderWrapper
            sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
          >
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Kennels</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
            >
              <BookingCardKennelList
                kennels={booking.kennels!}
                bookingId={booking.id}
                checkInDate={booking.checkInDate}
              />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Surcharges Section */}
      {(booking.surcharges?.length ?? 0) > 0 && (
        <>
          <SectionHeaderWrapper
            sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
          >
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Surcharges</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
            >
              <BookingCardSurchargeList surcharges={booking.surcharges!} />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Totals Section */}
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>Totals</FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>
      <FormFieldRowMultiline>
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <FieldInputContainerMultiline
          sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
        >
          <BookingCardTotals
            pets={booking.pets}
            kennels={booking.kennels}
            surcharges={booking.surcharges}
            depositAmount={booking.depositAmount}
            isQuotation={false}
          />
        </FieldInputContainerMultiline>
      </FormFieldRowMultiline>

      {/* Booking Form Email History */}
      {/* <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>Booking Form History</FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>
      <FormFieldRowMultiline>
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <FieldInputContainerMultiline
          sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
        >
          <BookingCardInvoiceHistory
            emailLog={bookingFormEmailLog}
            label="Booking Form"
            onSendNow={() => setBookingFormDialogOpen(true)}
            sendDisabled={!resolvedClientEmail}
          />
        </FieldInputContainerMultiline>
      </FormFieldRowMultiline> */}

      {/* Invoice Email History */}
      {canManage && (
        <>
          <SectionHeaderWrapper
            sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
          >
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Invoice Email History</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{ pb: 10, '@media (max-width: 600px)': { padding: 0 } }}
            >
              <BookingCardInvoiceHistory
                emailLog={emailLog}
                label="Invoice"
                onSendNow={() => setEmailDialogOpen(true)}
                sendDisabled={!resolvedClientEmail}
              />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Metadata */}
      <ResponsiveFormGrid
        breakpointPadding={{
          default: '0.5rem',
          medium: '0.03rem',
          large: '1rem',
        }}
        container
        spacing={0}
        sx={{ marginTop: 0, '@media (max-width: 600px)': { marginTop: 5 } }}
      >
        <MetadataField
          label={`Booking #${booking.id}`}
          labelFontSize="2rem"
          labelFontSizeMobile="1.8rem"
          labelFontWeight={700}
          valueFontSizeMobile="1.4rem"
          value={getBookingMetadataValue(booking)}
          hideColon
          valuePaddingMobile="0.5rem 1.5rem 5rem 1.5rem"
        />
      </ResponsiveFormGrid>

      {canManage && !isHeaderDrawerOpen && (
        <FloatingAccessButton
          ref={bottomMenuFabRef}
          icon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
          hoverIcon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
          onClick={() => setBottomMenuOpen((v) => !v)}
          iconScale={2}
          aria-label={
            bottomMenuOpen ? 'Close actions menu' : 'Open actions menu'
          }
          backgroundColor={
            bottomMenuOpen
              ? theme.palette.secondary.main
              : theme.palette.primary.main
          }
          hoverBackgroundColor={
            bottomMenuOpen
              ? theme.palette.primary.dark
              : theme.palette.secondary.dark
          }
          bottom="2rem"
          right="10%"
          sx={{
            zIndex: 1300,
            border: '2px solid #fff',
            '@media (max-width: 600px)': {
              bottom: '1rem',
              right: '1rem',
              '& svg': { transform: 'scale(1.2)' },
            },
            '@media (min-width: 601px)': {
              width: '75px',
              height: '75px',
              minWidth: '75px',
              minHeight: '75px',
            },
            '& svg': {
              transition: 'transform 0.3s ease-in-out',
              transform: 'scale(1.4)',
            },
            '&:active': {
              backgroundColor: bottomMenuOpen
                ? 'secondary.light'
                : 'primary.dark',
            },
            '&:hover svg': {
              opacity: 0.8,
              transform: bottomMenuOpen
                ? 'scale(2.1) rotate(-180deg)'
                : 'scale(2.1) rotate(180deg)',
            },
          }}
        />
      )}

      {canManage && (
        <BottomMenu
          open={bottomMenuOpen}
          onClose={() => setBottomMenuOpen(false)}
          items={bookingMenuItems}
          right={bottomMenuAnchor ? `${bottomMenuAnchor.right}px` : '1rem'}
          bottom={bottomMenuAnchor ? `${bottomMenuAnchor.bottom}px` : '40px'}
          sx={{
            '@media (max-width: 900px)': {
              marginRight: '.1rem',
              marginBottom: '.1rem',
            },
            '@media (min-width: 900px)': {
              marginRight: '1rem',
              marginBottom: '1rem',
            },
          }}
        />
      )}

      {/* Dialogs */}
      <EmailInvoiceDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onConfirm={handleEmailInvoice}
        clientName={booking.clientName}
        clientEmail={resolvedClientEmail ?? ''}
        sending={emailSending}
      />

      <EmailBookingFormDialog
        open={bookingFormDialogOpen}
        onClose={() => setBookingFormDialogOpen(false)}
        onConfirm={handleSendBookingForm}
        clientName={booking.clientName}
        clientEmail={resolvedClientEmail ?? ''}
        sending={bookingFormSending}
      />
    </StyledCard>
  );
};

export default BookingDetailCard;
